/*
 * <hero-course-search> — the homepage's course entry point.
 *
 * Searches the same catalogue as the product-page designer
 * (assets/course-designer.js) but does none of the rendering: it captures
 * WHICH course the customer means, then hands that choice to a product page
 * in the URL, where <course-designer> picks it up and generates the design.
 * Deliberately a separate, small element rather than a second designer — the
 * designer is bound to a product form (line-item properties, overlay, proof
 * baking) and none of that exists here.
 *
 * The garment buttons are the section's own `buttons` block, not ours: this
 * rewrites their links rather than conjuring new buttons of its own, and the
 * CSS slides them out from behind the plate. That block is a sibling inside
 * .banner__box, which is why this reaches outside itself to find them.
 *
 * Two shapes. On a desktop the field is on the hero. On a phone the hero shows
 * a trigger and this opens as a full-screen sheet — the field was hidden below
 * 750px entirely until now, which left the majority of visitors landing on a
 * headline and two generic garment buttons with no way in. The sheet also
 * mirrors those garment buttons as its second step, so a phone customer runs
 * course -> garment -> product page without leaving one surface.
 *
 * The hero can also fire /api/prewarm — from the garment-link CLICK, not from
 * the selection. A prewarm is a full render on a single-process API, so warming
 * every course somebody browses puts real work behind a guess: one customer
 * looked at four courses and then queued behind their own three discarded
 * renders. The click is where the choice becomes real, and it opens the only
 * window worth hiding a render in: the ~2s walk to the product page, which
 * auto-generates the moment it lands.
 *
 * It is OFF by default (theme setting "Prewarm the chosen course on click").
 * The API answers fast enough now that there is little left to hide, so the
 * speculative render costs the queue more than it saves. Kept wired, not
 * deleted: if a slow case makes it worth paying for again, the setting is the
 * only thing that has to change.
 */
(function () {
  'use strict';

  if (customElements.get('hero-course-search')) return;

  // Whether this deployment's API answers /api/nearby, cached for the session.
  // Same key the product-page designer uses: one probe answers for both, so a
  // customer who came in through the hero doesn't pay for a second one.
  var NEARBY_KEY = 'tothenines:nearby-supported';
  // The radius ladder, widened a rung at a time by pulling past the end of the
  // list. See course-designer.js for why the limit has to climb with it.
  var NEARBY_STEPS = [
    { radius_km: 80, limit: 10 },
    { radius_km: 250, limit: 25 },
    { radius_km: 500, limit: 25 },
  ];
  var NEARBY_RADIUS_KM = NEARBY_STEPS[0].radius_km;
  // The last line has to be careful not to sound like the end of the catalogue —
  // see course-designer.js. What has run out is golf near the customer, not
  // courses, and the field above is the right tool for the rest of them.
  var NEARBY_MORE_LABELS = {
    more: 'Search further out',
    loading: 'Searching further out…',
    end: 'Nothing more nearby. Type a course name to find it.',
  };
  var NEARBY_LOCATING = 'Finding your location…';
  var NEARBY_DENIED =
    'Location is off for this site. Turn it on in your browser, or type the course name.';
  var NEARBY_NO_FIX = "Couldn't get a location fix. Try again, or type the course name.";
  var NEARBY_HEADING = 'Nearest to you';

  // The hero and the pill are two instances holding one choice: a course picked
  // in either is the course in both. Announced on the document, not between the
  // two elements, so neither has to know the other exists.
  var CHOSEN_EVENT = 'hero-course-search:chosen';
  var CLEARED_EVENT = 'hero-course-search:cleared';
  // A desktop hero has no garment step of its own, so after a pick it asks the
  // pill to open its card straight onto that step.
  var GARMENTS_EVENT = 'hero-course-search:garments';

  // All of these are lifted from course-designer.js, which owns the canonical
  // versions. Kept in step by hand: the API's result shape is the contract, not
  // this file.
  function courseNameOf(item) {
    if (item.name) return item.name;
    var club = item.club_name || '';
    var course = item.course_name || '';
    if (club && course && club !== course) return club + ' — ' + course;
    if (club || course) return club || course;
    return item.label || '';
  }

  function coursePlaceOf(item) {
    if (item.place) return item.place;
    if (item.name) return '';
    var label = item.label || '';
    var match = /\s\(([^()]*)\)$/.exec(label);
    if (!match) return '';
    return label.slice(0, match.index) === courseNameOf(item) ? match[1] : '';
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // ---- How far away a course is (see course-designer.js for the reasoning) --

  function usesMiles() {
    var tags = navigator.languages && navigator.languages.length
      ? navigator.languages
      : [navigator.language || ''];
    for (var i = 0; i < tags.length; i++) {
      if (/-(GB|US|MM|LR)\b/i.test(tags[i])) return true;
    }
    return false;
  }

  var KM_PER_MILE = 1.609344;
  var EARTH_RADIUS_KM = 6371;

  function distanceKm(lat1, lon1, lat2, lon2) {
    var rad = Math.PI / 180;
    var dLat = (lat2 - lat1) * rad;
    var dLon = (lon2 - lon1) * rad;
    var a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(a)));
  }

  function formatDistance(km) {
    var miles = usesMiles();
    var value = miles ? km / KM_PER_MILE : km;
    var unit = miles ? 'mi' : 'km';
    return (value < 10 ? value.toFixed(1) : String(Math.round(value))) + ' ' + unit;
  }

  function radiusLabel() {
    return usesMiles()
      ? Math.round(NEARBY_RADIUS_KM / KM_PER_MILE) + ' miles'
      : NEARBY_RADIUS_KM + ' kilometres';
  }

  // A single box-shadow as getComputedStyle writes it ('rgba(r, g, b, a) x y
  // blur spread'), as numbers — or null for none or anything fancier.
  function parseShadow(value) {
    var m = /rgba?\(([^)]+)\)\s+(-?[\d.]+)px\s+(-?[\d.]+)px\s+([\d.]+)px/.exec(value || '');
    if (!m) return null;
    var c = m[1].split(',').map(function (n) { return parseFloat(n); });
    return { r: c[0], g: c[1], b: c[2], a: c.length > 3 ? c[3] : 1, x: +m[2], y: +m[3], blur: +m[4] };
  }

  function coordsOf(item) {
    var loc = item && item.location;
    if (!loc) return null;
    var lat = typeof loc.latitude === 'number' ? loc.latitude : null;
    var lon = typeof loc.longitude === 'number' ? loc.longitude : null;
    return lat === null || lon === null ? null : { lat: lat, lon: lon };
  }

  class HeroCourseSearch extends HTMLElement {
    connectedCallback() {
      if (this._wired) return;
      this._wired = true;

      this.apiBase = (this.dataset.apiBase || '').replace(/\/+$/, '');
      // Off unless the theme setting turns it back on — see bind().
      this.prewarmEnabled = this.dataset.prewarm === 'true';
      this.searchEl = this.q('search');
      this.suggestionsEl = this.q('suggestions');
      this.ghostEl = this.q('ghost');
      this.ghostTextEl = this.q('ghost-text');
      this.statusEl = this.q('status');
      this.spinnerTpl = this.q('spinner');
      // The phone shape: a trigger on the hero, the field in a sheet behind it.
      this.triggerEl = this.q('trigger');
      this.triggerGhostEl = this.q('trigger-ghost');
      this.sheetEl = this.q('sheet');
      this.closeEl = this.q('close');
      this.garmentsEl = this.q('garments');
      this.garmentLinksEl = this.q('garment-links');
      // Courses near the customer. On a desktop this sits at the right-hand end
      // of the ruled line; inside the phone sheet it is the ruled row beneath
      // it. One control, two shapes — the same trick the element already plays
      // with the field itself.
      this.nearbyEl = this.q('nearby');
      this.nearbyLabelEl = this.q('nearby-label');
      this.nearbyNoteEl = this.q('nearby-note');
      // The docked pill (mode 'pill' in the snippet): the same element again,
      // outside the banner, whose sheet is a dialog at every width.
      this.isPill = this.dataset.mode === 'pill';
      this.backdropEl = this.q('backdrop');
      // The banner's own buttons, one level up in .banner__box — or, for the
      // pill, which is nowhere near them, the copies the snippet wrote into it.
      var box = this.isPill ? this.q('links') : (this.closest('.banner__box') || this.parentElement);
      this.links = box
        ? Array.prototype.slice.call(box.querySelectorAll(this.isPill ? 'a.button' : '.banner__buttons a.button'))
        : [];
      // The unparameterised product URLs, kept so a second pick rewrites from
      // the original rather than appending to the last one's query string.
      this.baseHrefs = this.links.map(function (a) {
        return a.getAttribute('href') || '';
      });

      this._acDebounce = null;
      this._acAbort = null;
      this._acItems = [];
      this._acActive = -1;
      // The course currently written into the links, and whether its prewarm
      // has already gone. Both are owned by selectSuggestion/clearChoice, so
      // the links and the warm can never describe different courses.
      this._selection = null;
      this._prewarmed = false;

      if (!this.searchEl || !this.suggestionsEl) return;

      this.bind();
      this.startGhost();

      // With the pill on the page, a desktop pick goes on to its card, so the
      // banner's buttons have no step left to play (component-hero-course-
      // search.css hides them). Without it they slide out as the choice.
      if (!this.isPill && document.querySelector('hero-course-search[data-mode="pill"]')) {
        this.classList.add('hero-course-search--funnel');
      }
    }

    disconnectedCallback() {
      clearTimeout(this._acDebounce);
      clearTimeout(this._ghostTimer);
      if (this._acAbort) this._acAbort.abort();
      if (this._onVisibility) {
        document.removeEventListener('visibilitychange', this._onVisibility);
      }
      if (this._mq && this._onBreakpoint) {
        this._mq.removeEventListener('change', this._onBreakpoint);
      }
      if (this._onSheetKey) document.removeEventListener('keydown', this._onSheetKey);
      if (this._onPageShow) window.removeEventListener('pageshow', this._onPageShow);
      if (this._onOpener) document.removeEventListener('click', this._onOpener);
      if (this._onGarments) document.removeEventListener(GARMENTS_EVENT, this._onGarments);
      if (this._onShared) {
        document.removeEventListener(CHOSEN_EVENT, this._onShared);
        document.removeEventListener(CLEARED_EVENT, this._onShared);
      }
      if (this._onMorphScroll) {
        window.removeEventListener('scroll', this._onMorphScroll);
        window.removeEventListener('resize', this._onMorphScroll);
        cancelAnimationFrame(this._morphFrame);
        if (this._hero) this._hero.classList.remove('is-pilled');
      }
      this.clearPrefetch();
      // A sheet that is torn down while open would leave the page unscrollable.
      if (this.classList.contains('is-open')) {
        document.body.style.overflow = this._prevBodyOverflow || '';
      }
    }

    q(ref) {
      return this.querySelector('[data-ref="' + ref + '"]');
    }

    // The input is a combobox, so its aria-expanded has to track the dropdown
    // exactly. One method owns both halves rather than trusting every caller
    // to remember the second one.
    setSuggestionsOpen(open) {
      this.suggestionsEl.hidden = !open;
      this.searchEl.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (!open) this.searchEl.removeAttribute('aria-activedescendant');
    }

    bind() {
      this.searchEl.addEventListener('input', this.onSearchInput.bind(this));
      this.searchEl.addEventListener('keydown', this.onSearchKeydown.bind(this));
      this.searchEl.addEventListener('focus', this.onFocus.bind(this));
      this.searchEl.addEventListener('blur', this.onBlur.bind(this));

      if (this.nearbyEl) this.nearbyEl.addEventListener('click', this.onNearbyClick.bind(this));

      if (this.triggerEl) this.triggerEl.addEventListener('click', this.openSheet.bind(this));
      if (this.closeEl) {
        this.closeEl.addEventListener('click', function () { this.closeSheet(); }.bind(this));
      }
      // Escape from anywhere in the sheet, not just the field: the close button
      // and the garment links are both focusable and both need a way out.
      this._onSheetKey = function (e) {
        if (e.key === 'Escape') this.closeSheet();
      }.bind(this);

      // The customer leaving for the chosen course: show that the tap landed,
      // and warm the course if that is switched on. Listening rather than
      // wrapping the links keeps applyLinks the only thing that touches an
      // href, so the buttons still work with JS half-loaded, on a middle-click
      // and on open-in-new-tab.
      this.links.forEach(function (link) {
        link.addEventListener('click', this.onGarmentClick.bind(this));
      }.bind(this));

      // Back from the product page, a page restored from the back/forward
      // cache would still be showing the spinner it left with.
      this._onPageShow = function (e) {
        if (e.persisted) this.clearLoading();
      }.bind(this);
      window.addEventListener('pageshow', this._onPageShow);

      // Timers in a backgrounded tab buy nothing; the ghost picks up where it
      // left off when the tab comes back.
      this._onVisibility = function () {
        if (document.hidden) {
          clearTimeout(this._ghostTimer);
        } else if (this._ghosting) {
          this.tickGhost();
        }
      }.bind(this);
      document.addEventListener('visibilitychange', this._onVisibility);

      // Which shape this element is in. Both shapes ghost-type — on a desktop
      // into the field, on a phone into the trigger — so the breakpoint no
      // longer starts and stops the ghost; it decides whether the sheet is a
      // dialog. A resize can cross it in either direction, so applyShape runs
      // on change as well as once now.
      this._mq = window.matchMedia('(min-width: 750px)');
      this._onBreakpoint = this.applyShape.bind(this);
      this._mq.addEventListener('change', this._onBreakpoint);
      this.applyShape();

      // A pick in the other instance is a pick here too.
      this._onShared = this.onSharedChoice.bind(this);
      document.addEventListener(CHOSEN_EVENT, this._onShared);
      document.addEventListener(CLEARED_EVENT, this._onShared);

      if (this.isPill) this.bindPill();
    }

    // ---- The docked pill ---------------------------------------------------

    bindPill() {
      if (this.backdropEl) {
        this.backdropEl.addEventListener('click', function () { this.closeSheet(); }.bind(this));
      }

      // Any button elsewhere on the page that asks for the funnel — the prompt
      // under the design wall's comments. Delegated, so a section re-rendered in
      // the theme editor keeps working without re-binding. Still inside the
      // click, which is what lets openSheet raise the iOS keyboard.
      this._onOpener = function (e) {
        var opener = e.target.closest && e.target.closest('[data-course-funnel-open]');
        if (!opener) return;
        e.preventDefault();
        this.openSheet(opener);
      }.bind(this);
      document.addEventListener('click', this._onOpener);

      // The desktop hero's pick, carried on into the garment step here. The
      // choice itself has already arrived through CHOSEN_EVENT; focus goes
      // back to the hero's field when the card is closed.
      this._onGarments = function (e) {
        if (!e.detail || e.detail.source === this) return;
        this.openSheet(e.detail.source.searchEl, { garments: true });
      }.bind(this);
      document.addEventListener(GARMENTS_EVENT, this._onGarments);

      this._hero =document.querySelector('hero-course-search:not([data-mode="pill"])');
      this._pill = {
        eyebrow: this.q('pill-eyebrow'),
        text: this.q('pill-text'),
        from: this.q('pill-from'),
        fromText: this.q('pill-from-text'),
        fromCaret: this.q('pill-from-caret'),
        to: this.triggerGhostEl,
        rule: this.q('pill-rule'),
        pin: this.q('pill-pin'),
        mark: this.q('pill-mark'),
      };
      // One frame's work per scroll burst, whatever the event rate.
      this._onMorphScroll = function () {
        if (this._morphFrame) return;
        this._morphFrame = requestAnimationFrame(function () {
          this._morphFrame = null;
          this.morphPill();
        }.bind(this));
      }.bind(this);
      window.addEventListener('scroll', this._onMorphScroll, { passive: true });
      window.addEventListener('resize', this._onMorphScroll);
      this.morphPill();
    }

    // ---- The field becoming the pill ---------------------------------------
    //
    // Not an animation that plays when a line is crossed: the hero's field is
    // squeezed into the pill by the scroll itself, so it can be stopped half
    // way, and run backwards by scrolling back up. The field scrolls normally
    // until it is a field's height short of the pill's line at the top of the
    // screen. On that frame the pill takes over at exactly the field's size,
    // place and text, and the field is hidden behind it (visibility only — the
    // hero's layout must not move). From there it keeps riding up with the
    // page while each pixel of scroll compresses it, and arrives at the line
    // as the pill.
    //
    // Every rectangle is read live from the hero on each frame. A hidden
    // element still lays out, so the measurements hold however far the field
    // has gone off screen, and nothing is cached to go stale on a resize, a
    // font swap, or the nearby pin turning up late.

    // Where the pill sits: clear of the screen's top edge, and clear of the
    // sticky header while it has slid back in.
    pillTop() {
      var header = document.querySelector('.section-header');
      var shown = header &&
        header.classList.contains('shopify-section-header-sticky') &&
        !header.classList.contains('shopify-section-header-hidden');
      return 12 + (shown ? header.offsetHeight : 0);
    }

    // The hero's field as it is drawn at this width: the plate on a desktop,
    // the trigger on a phone. And within it, the label, the line the name is
    // written on, and (desktop only) the pin.
    heroField() {
      var hero = this._hero;
      if (this._mq.matches) {
        var plate = hero.querySelector('.hero-course-search__plate');
        var nearby = hero.querySelector('[data-ref="nearby"]');
        return plate && {
          box: plate,
          eyebrow: plate.querySelector(':scope > .hero-course-search__eyebrow'),
          line: plate.querySelector('.hero-course-search__field'),
          font: hero.querySelector('[data-ref="search"]'),
          pin: nearby && !nearby.hidden ? nearby.querySelector('.hero-course-search__nearby-mark') : null,
        };
      }
      var trigger = hero.querySelector('[data-ref="trigger"]');
      var line = trigger && trigger.querySelector('.hero-course-search__trigger-line');
      return trigger && {
        box: trigger,
        eyebrow: trigger.querySelector('.hero-course-search__eyebrow'),
        line: line,
        font: line,
        pin: null,
      };
    }

    // What the field is showing right now, so the pill starts with the same
    // words: the name typed or chosen, or the ghost mid-word.
    heroFieldText() {
      var hero = this._hero;
      if (hero._selection) return { text: hero._selection.name, ghost: false };
      if (this._mq.matches) {
        var value = hero.searchEl ? hero.searchEl.value : '';
        if (value) return { text: value, ghost: false };
        var ghost = hero.querySelector('[data-ref="ghost-text"]');
        return { text: ghost ? ghost.textContent : '', ghost: true };
      }
      var trigger = hero.querySelector('[data-ref="trigger-ghost"]');
      return { text: trigger ? trigger.textContent : '', ghost: !hero._ghostDone };
    }

    morphPill() {
      var hero = this._hero;
      var field = hero && this.heroField();
      if (!field || !field.line) {
        // No hero to grow out of: the pill is simply there.
        this.classList.add('is-docked');
        this.triggerEl.style.top = this.pillTop() + 'px';
        this.applyPill(null, 1);
        return;
      }

      var F = field.box.getBoundingClientRect();
      var top = this.pillTop();
      // The squeeze runs over the last stretch of the field's way up, ending
      // as its top edge meets the pill's line — a field's height of scroll, or
      // 80px for a short one. Through that stretch the pill rides up with the
      // field rather than waiting at the line for it: a box that is moving with
      // the page and then stops dead to change shape reads as a jump, where one
      // that keeps moving while it reshapes reads as the same box. It only
      // comes to rest at the line once it is a pill, which is the point a
      // sticky element would stop anyway. With reduced motion there is no
      // stretch: field until the line, pill after it.
      var run = prefersReducedMotion() ? 0 : Math.max(80, F.height);
      // A page loaded part-way down has the field far above the line, which is
      // simply the fully-squeezed end.
      var docked = F.height > 0 && F.top <= top + run;
      this.classList.toggle('is-docked', docked);
      hero.classList.toggle('is-pilled', docked);
      if (!docked) return;

      var p = run ? Math.min(1, (top + run - F.top) / run) : 1;
      // Set here rather than left to the stylesheet, so the line the field is
      // measured against and the line the pill sits on are one number. Eased
      // only once it has settled (is-settled), when the one thing that moves it
      // is the sticky header sliding in or out; while it rides with the field,
      // anything easing behind the scroll would lag it.
      this.triggerEl.classList.toggle('is-settled', p === 1);
      this.triggerEl.style.top = Math.max(top, F.top) + 'px';
      this.applyPill(field, p, F, top);
    }

    // Puts every part of the pill at progress p: 0 is the hero's field, 1 is
    // the pill. Parts that exist only in one of the two fade over the part of
    // the run where the other is still recognisably itself.
    applyPill(field, p, F, top) {
      var el = this._pill;
      var trigger = this.triggerEl;
      var vw = document.documentElement.clientWidth;
      // The pill's own geometry, in px.
      var W1 = Math.min(400, vw - 32);
      var H1 = 56;
      var PAD1 = 22;
      var MARK = 44;
      var MARK_INSET = 6;
      var FONT1 = 16;
      var LH1 = 24;

      function lerp(a, b) { return a + (b - a) * p; }
      // A fade over [from, to] of the run, 0 before and 1 after.
      function over(from, to) { return Math.max(0, Math.min(1, (p - from) / (to - from))); }

      // With no field to start from, start where it ends.
      var L = field ? field.line.getBoundingClientRect() : null;
      var E = field && field.eyebrow ? field.eyebrow.getBoundingClientRect() : null;
      var P = field && field.pin ? field.pin.getBoundingClientRect() : null;
      var font0 = field ? parseFloat(getComputedStyle(field.font).fontSize) || FONT1 : FONT1;
      var lh0 = field ? parseFloat(getComputedStyle(field.font).lineHeight) || LH1 : LH1;
      var x0 = F ? F.left + F.width / 2 : vw / 2;
      var w0 = F ? F.width : W1;
      var h0 = F ? F.height : H1;

      var w = lerp(w0, W1);
      var h = lerp(h0, H1);
      trigger.style.left = lerp(x0, vw / 2) + 'px';
      trigger.style.width = w + 'px';
      trigger.style.height = h + 'px';
      trigger.style.borderRadius = (H1 / 2) * p + 'px';

      // The shadow too, or it flips at the swap: the phone hero's bar casts
      // upwards onto the film, the pill downwards onto the page. The pill's own
      // is read once, before anything inline has replaced it.
      if (this._pillShadow === undefined) this._pillShadow = parseShadow(getComputedStyle(trigger).boxShadow);
      var s0 = field ? parseShadow(getComputedStyle(field.box).boxShadow) : null;
      var s1 = this._pillShadow;
      if (s0 && s1) {
        trigger.style.boxShadow = lerp(s0.x, s1.x) + 'px ' + lerp(s0.y, s1.y) + 'px ' +
          lerp(s0.blur, s1.blur) + 'px rgba(' + Math.round(lerp(s0.r, s1.r)) + ',' +
          Math.round(lerp(s0.g, s1.g)) + ',' + Math.round(lerp(s0.b, s1.b)) + ',' + lerp(s0.a, s1.a) + ')';
      }

      // The words: from the field's line to the pill's, shrinking as they go.
      var textX0 = L ? L.left - F.left : PAD1;
      var textY0 = L ? L.top - F.top : (H1 - LH1) / 2;
      var textW0 = L ? L.width : W1 - PAD1 - MARK - MARK_INSET - 12;
      var lh = lerp(lh0, LH1);
      el.text.style.left = lerp(textX0, PAD1) + 'px';
      el.text.style.top = lerp(textY0, (H1 - LH1) / 2) + 'px';
      el.text.style.width = lerp(textW0, W1 - PAD1 - MARK - MARK_INSET - 12) + 'px';
      el.text.style.fontSize = lerp(font0, FONT1) + 'px';
      el.text.style.lineHeight = lh + 'px';

      if (field) {
        var shown = this.heroFieldText();
        if (el.fromText.textContent !== shown.text) el.fromText.textContent = shown.text;
        el.from.classList.toggle('is-ghost', shown.ghost);
        el.fromCaret.hidden = !shown.ghost;
      }
      el.from.style.opacity = 1 - over(0.15, 0.55);
      el.to.style.opacity = over(0.3, 0.75);

      // Only the field has a label and a rule; they go early, while there is
      // still room for them.
      if (E) {
        el.eyebrow.style.left = E.left - F.left + 'px';
        el.eyebrow.style.top = (E.top - F.top) * (1 - p) + 'px';
      }
      el.eyebrow.style.opacity = E ? 1 - over(0, 0.35) : 0;

      if (L) {
        el.rule.style.left = el.text.style.left;
        el.rule.style.top = lerp(L.bottom - F.top - 1, H1 - 1) + 'px';
        el.rule.style.width = el.text.style.width;
        el.rule.classList.toggle('is-chosen', !!this._hero._selection);
      }
      el.rule.style.opacity = L ? 1 - over(0, 0.4) : 0;

      // The pin gives way to the search mark in the same corner.
      var markX = w - MARK_INSET - MARK;
      var markY = (h - MARK) / 2;
      if (P) {
        el.pin.style.left = lerp(P.left - F.left, markX + (MARK - P.width) / 2) + 'px';
        el.pin.style.top = lerp(P.top - F.top, markY + (MARK - P.height) / 2) + 'px';
      }
      el.pin.style.opacity = P ? 1 - over(0.1, 0.45) : 0;
      var m = over(0.3, 0.8);
      // Placed by the stylesheet, against the pill's own right edge and middle,
      // not from the numbers above: worked out here it sat off-centre in the
      // settled pill. Only its arrival is the script's.
      el.mark.style.opacity = m;
      el.mark.style.transform = 'translateY(-50%) scale(' + (0.4 + 0.6 * m) + ')';
    }

    // The other instance picked or cleared a course. Take it over without
    // re-announcing it: the live region has already said so once.
    onSharedChoice(e) {
      if (!e.detail || e.detail.source === this) return;
      if (e.type === CLEARED_EVENT) {
        if (!this._selection) return;
        this.searchEl.value = '';
        this.clearChoice({ shared: true });
        // Back to demonstrating, since there is nothing left in the field.
        this._ghostDone = false;
        if (this.ghostEl) this.ghostEl.hidden = false;
        this.startGhost();
        return;
      }
      var sel = e.detail.selection;
      this.searchEl.value = sel.name;
      this.setSuggestionsOpen(false);
      this._acItems = [];
      this.stopGhost();
      this._selection = Object.assign({}, sel);
      this._prewarmed = false;
      this.applyLinks(this._selection);
      this.classList.add('is-chosen');
      this.syncTrigger();
      if (this.endsOnGarments()) {
        this.renderGarmentLinks();
        if (this.garmentsEl) this.garmentsEl.hidden = false;
      }
    }

    // Whether a pick moves straight on to the garment step: always for the
    // pill, and on a phone for the hero. A desktop hero reveals its own
    // buttons instead.
    endsOnGarments() {
      return this.isPill || (this._mq && !this._mq.matches);
    }

    // Elsewhere on the page that shows this element's state: the pill's
    // prompts, each a still line that holds the course once one is chosen.
    promptLines() {
      if (!this.isPill) return [];
      return Array.prototype.slice.call(document.querySelectorAll('[data-course-funnel-ghost]'));
    }

    // ---- The phone sheet ---------------------------------------------------

    // ARIA has to describe the shape that is actually on screen: role="dialog"
    // on an element that is display: contents announces a dialog the customer
    // can never be inside. The attributes are added on a phone and taken away
    // again above 750px, and a sheet left open across a resize is closed.
    applyShape() {
      if (!this.sheetEl) return;
      // The pill's sheet is a dialog at every width — a centred card on a
      // desktop — so it neither loses its role nor closes on a resize.
      if (this._mq.matches && !this.isPill) {
        this.closeSheet({ silent: true });
        this.sheetEl.removeAttribute('role');
        this.sheetEl.removeAttribute('aria-modal');
      } else {
        this.sheetEl.setAttribute('role', 'dialog');
        this.sheetEl.setAttribute('aria-modal', 'true');
      }
    }

    // `opener` is the control focus goes back to on close. A click handler
    // passes its event, which isn't one, so only an element counts.
    // `opts.garments`: open on the garment step, the course already chosen.
    openSheet(opener, opts) {
      if ((this._mq.matches && !this.isPill) || this.classList.contains('is-open')) return;
      this._opener = opener instanceof HTMLElement ? opener : this.triggerEl;
      this.classList.add('is-open');
      // Not on the hero's field, which is a combobox: its aria-expanded is
      // its own suggestions list, and that is closed.
      if (this._opener && !(opts && opts.garments)) this._opener.setAttribute('aria-expanded', 'true');
      // The page behind a full-screen sheet must not scroll under it.
      this._prevBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', this._onSheetKey);
      this.warmGarmentPreviews();
      if (opts && opts.garments && this._selection) {
        this.showGarmentStep();
        // The card itself, not its first garment: a ring on one card reads as
        // that garment already chosen. The next Tab reaches the first anyway.
        this.sheetEl.setAttribute('tabindex', '-1');
        this.sheetEl.focus({ preventScroll: true });
        return;
      }
      // Synchronously, inside the click that opened it: iOS only raises the
      // keyboard for a focus() that is still inside the user gesture, and a
      // sheet whose whole point is the keyboard must not open without it.
      this.searchEl.focus();
    }

    // The garment previews are a few hundred KB each and only ever shown in
    // this sheet, so they are fetched when it opens rather than with the page:
    // a desktop never pays for them, and by the time a course is picked on a
    // phone they are in the cache and step two appears already moving.
    warmGarmentPreviews() {
      if (this._previewsWarmed) return;
      this._previewsWarmed = true;
      this.links.forEach(function (link, i) {
        var preview = this.garmentPreview(i);
        if (preview) new Image().src = preview;
      }.bind(this));
    }

    garmentPreview(i) {
      return (this.garmentsEl && this.garmentsEl.getAttribute('data-preview-' + (i + 1))) || '';
    }

    closeSheet(opts) {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      var opener = this._opener || this.triggerEl;
      if (opener) opener.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = this._prevBodyOverflow || '';
      document.removeEventListener('keydown', this._onSheetKey);
      this.setSuggestionsOpen(false);
      // Focus goes back where it came from — unless the sheet is being closed
      // by a resize into the desktop layout, where the trigger is not on screen.
      // preventScroll: the pill's prompt can be a screen away from where the
      // customer closed the card, and focus would scroll the page to it.
      if (!(opts && opts.silent) && opener) opener.focus({ preventScroll: true });
    }

    // The trigger is the phone's view of this element's state. Empty, it
    // ghost-types course names; once a course is picked it holds that course, so
    // the hero reads back what the customer said rather than resetting itself.
    // The pill and its prompts elsewhere on the page never ghost-type: they
    // show their own still text (rendered into them, kept here the first time
    // they are touched) until a course is chosen, and go back to it after.
    // Prompts carry is-chosen on their opener for their own stylesheet to read.
    syncTrigger() {
      var chosen = this._selection && this._selection.name;
      this.promptLines().forEach(function (line) {
        var opener = line.closest('[data-course-funnel-open]');
        if (opener) opener.classList.toggle('is-chosen', !!chosen);
        this.holdOrRestore(line, chosen);
      }.bind(this));
      if (!this.triggerGhostEl || !this.triggerEl) return;
      this.triggerEl.classList.toggle('is-chosen', !!chosen);
      if (this.isPill) {
        this.holdOrRestore(this.triggerGhostEl, chosen);
        return;
      }
      if (chosen) {
        this.triggerGhostEl.textContent = chosen;
        return;
      }
      if (this._ghostDone) this.triggerGhostEl.textContent = this.searchEl.value.trim();
    }

    holdOrRestore(el, chosen) {
      if (el.dataset.idleText === undefined) el.dataset.idleText = el.textContent.trim();
      el.textContent = chosen || el.dataset.idleText;
    }

    // Phone step two: the suggestions give way to the garment choice in the
    // same sheet, so the customer never returns to the hero to find the buttons.
    showGarmentStep() {
      if (!this.garmentsEl || !this.garmentLinksEl) return;
      this.setSuggestionsOpen(false);
      // Take the keyboard down: there is nothing left to type, and it would
      // cover the buttons this step exists to show.
      this.searchEl.blur();
      this.renderGarmentLinks();
      this.garmentsEl.hidden = false;
    }

    hideGarmentStep() {
      if (this.garmentsEl) this.garmentsEl.hidden = true;
    }

    // Mirrors of the section's own garment buttons, built from them so the
    // labels and the button styling are whatever the store owner set, and
    // rebuilt on each pick so the hrefs are the ones applyLinks just wrote.
    renderGarmentLinks() {
      this.garmentLinksEl.innerHTML = '';
      this.links.forEach(function (link, i) {
        var a = document.createElement('a');
        a.className = link.className + ' hero-course-search__garment';
        // The garment's look, above its name. Decorative: the label beside it
        // already says what the link is.
        var preview = this.garmentPreview(i);
        if (preview) {
          var img = document.createElement('img');
          img.className = 'hero-course-search__garment-preview';
          img.src = preview;
          img.alt = '';
          img.width = 400;
          img.height = 500;
          img.decoding = 'async';
          a.appendChild(img);
        }
        var label = document.createElement('span');
        label.className = 'hero-course-search__garment-label';
        label.textContent = (link.textContent || '').trim();
        a.appendChild(label);
        var href = link.getAttribute('href');
        if (href) a.setAttribute('href', href);
        // On a phone this mirror is the link that actually gets clicked, so the
        // loading state and the prewarm have to ride it as well as the original.
        a.addEventListener('click', this.onGarmentClick.bind(this));
        this.garmentLinksEl.appendChild(a);
      }.bind(this));
    }

    // ---- Ghost typing -----------------------------------------------------
    //
    // An empty search box asks a question ("what counts as a course?") that a
    // placeholder answers once and then stops answering. Typing the great
    // courses out, one after another, answers it continuously and makes the
    // field look already in use. It stops for good the moment the customer
    // touches the field — nothing types over someone who is typing.

    ghostNames() {
      // Newline-separated, not comma: plenty of clubs have a comma in the
      // name the store owner would reasonably type.
      return (this.dataset.ghostCourses || '')
        .split(/\r?\n/)
        .map(function (s) { return s.trim(); })
        .filter(Boolean);
    }

    // The list is a set, not a running order: read top to bottom, the same
    // three courses are the only ones most visitors ever see, and a second
    // visit looks like a recording. Shuffled instead, and reshuffled at the
    // end of every pass, so a pass still covers the whole list exactly once
    // — independent random draws would repeat and starve names in equal
    // measure.
    shuffle(list) {
      var out = list.slice();
      for (var i = out.length - 1; i > 0; i--) {
        var j = Math.floor(Math.random() * (i + 1));
        var swap = out[i];
        out[i] = out[j];
        out[j] = swap;
      }
      // Two passes must not butt the same name against itself at the seam.
      if (out.length > 1 && out[0] === this._lastName) out.push(out.shift());
      return out;
    }

    // One name, both surfaces: the field's ghost layer on a desktop and the
    // trigger's line on a phone. Only one of the two is ever on screen, and
    // writing to both is cheaper than deciding which.
    writeGhost(text) {
      if (this.ghostTextEl) this.ghostTextEl.textContent = text;
      // Never over a course the customer has already chosen — the trigger is
      // holding that, and typing over it would look like it had been lost.
      // The pill and its prompts hold still: they say what they do and leave
      // the demonstrating to the field inside the sheet.
      if (this.isPill) return;
      if (this.triggerGhostEl && !this._selection) this.triggerGhostEl.textContent = text;
    }

    startGhost() {
      if (!this.ghostEl || !this.ghostTextEl) return;
      if (this._ghostDone) return;
      this._names = this.shuffle(this.ghostNames());
      if (!this._names.length) {
        this.ghostEl.hidden = true;
        return;
      }
      // Reduced motion still needs the example — it just gets it as a single
      // static name rather than as something moving in the corner of the eye.
      if (prefersReducedMotion()) {
        this.writeGhost(this._names[0]);
        return;
      }
      this._gi = 0;
      this._gc = 0;
      this._deleting = false;
      this._ghosting = true;
      this.tickGhost();
    }

    tickGhost() {
      if (!this._ghosting) return;
      clearTimeout(this._ghostTimer);

      var name = this._names[this._gi];
      var delay;

      if (this._deleting) {
        this._gc -= 1;
        delay = 26;
        if (this._gc <= 0) {
          this._gc = 0;
          this._deleting = false;
          this._gi += 1;
          if (this._gi >= this._names.length) {
            this._lastName = name;
            this._names = this.shuffle(this._names);
            this._gi = 0;
          }
          delay = 420;
        }
      } else {
        this._gc += 1;
        // Jitter: an even cadence reads as a machine, which is exactly the
        // impression this is here to avoid.
        delay = 52 + Math.random() * 46;
        if (this._gc >= name.length) {
          this._deleting = true;
          delay = 1700;
        }
      }

      this.writeGhost(name.slice(0, this._gc));
      this._ghostTimer = setTimeout(this.tickGhost.bind(this), delay);
    }

    pauseGhost() {
      this._ghosting = false;
      clearTimeout(this._ghostTimer);
      if (this.ghostEl) this.ghostEl.hidden = true;
    }

    // Focus alone only pauses it: someone tabbing through the page shouldn't
    // permanently deaden the hero. A pick, or a keystroke, ends it.
    stopGhost() {
      this.pauseGhost();
      this._ghostDone = true;
    }

    onFocus() {
      this.pauseGhost();
      // One request, at the moment the customer engages with the field —
      // which on a phone is the sheet opening, since that focuses the input.
      this.probeNearby();
    }

    onBlur() {
      // A nearby run keeps both the list and the ghost as they are: the
      // browser's permission prompt takes the focus itself, so this blur is the
      // customer using the feature rather than leaving it, and a field that
      // started ghost-typing underneath the answer would look like it had
      // forgotten what it was doing.
      if (this._nearbyPinned) return;
      // Late enough for an option's mousedown to have run first.
      setTimeout(function () {
        if (this._nearbyPinned) return;
        this.setSuggestionsOpen(false);
      }.bind(this), 120);
      if (this._ghostDone || this.searchEl.value.trim()) return;
      if (prefersReducedMotion()) return;
      this._ghosting = true;
      this.ghostEl.hidden = false;
      this.tickGhost();
    }

    // ---- Courses near the customer -----------------------------------------
    //
    // The hero's shortcut past the keyboard, and the reason it matters most
    // here: this is a phone visitor's first screen, and typing a club name into
    // a hero over a photograph is the step most of them were going to abandon.
    // It captures a course exactly as picking a suggestion does — the product
    // page still does the generating.

    // The endpoint is newer than this theme, so the control has to earn its
    // place rather than assume it. Answered once per session and shared with
    // the product-page designer through one sessionStorage key.
    probeNearby() {
      if (!this.nearbyEl || !this.apiBase || this._nearbyProbed) return;
      this._nearbyProbed = true;
      if (!navigator.geolocation) return;
      var cached = null;
      try { cached = window.sessionStorage.getItem(NEARBY_KEY); } catch (e) {}
      if (cached === 'no') return;
      if (cached === 'yes') {
        this.nearbyEl.hidden = false;
        return;
      }
      fetch(this.apiBase + '/api/nearby?lat=0&lon=0&limit=1')
        .then(function (r) {
          try { window.sessionStorage.setItem(NEARBY_KEY, r.ok ? 'yes' : 'no'); } catch (e) {}
          if (r.ok) this.nearbyEl.hidden = false;
        }.bind(this))
        .catch(function () { /* offline, or no API: the field still works */ });
    }

    // The control is its own progress indicator — the reticle pulses and the
    // label says what is happening, in the place the customer just tapped.
    setNearbyBusy(busy) {
      if (!this.nearbyEl) return;
      this.nearbyEl.classList.toggle('is-locating', busy);
      this.nearbyEl.disabled = busy;
      this.nearbyEl.setAttribute('aria-busy', busy ? 'true' : 'false');
      if (this.nearbyLabelEl) {
        if (busy) this._nearbyIdleLabel = this.nearbyLabelEl.textContent;
        this.nearbyLabelEl.textContent = busy
          ? NEARBY_LOCATING
          : (this._nearbyIdleLabel || this.nearbyLabelEl.textContent);
      }
    }

    // Why there is no list. Inside the sheet it stops the field growing to fill
    // the screen, so the line lands under the control rather than a phone's
    // height below it.
    setNearbyNote(text) {
      if (!this.nearbyNoteEl) return;
      this.nearbyNoteEl.textContent = text || '';
      this.nearbyNoteEl.hidden = !text;
      this.classList.toggle('has-nearby-note', !!text);
    }

    onNearbyClick() {
      if (!navigator.geolocation) return;
      this._nearbyPinned = true;
      this.stopGhost();
      this.setNearbyNote('');
      this.setNearbyBusy(true);
      navigator.geolocation.getCurrentPosition(
        function (pos) {
          this.fetchNearby(pos.coords.latitude, pos.coords.longitude);
        }.bind(this),
        function (err) {
          this._nearbyPinned = false;
          this.setNearbyBusy(false);
          // Refusing again is all a retry would achieve for a denied
          // permission, so that one gets the instruction and the rest the retry.
          this.setNearbyNote(err && err.code === 1 ? NEARBY_DENIED : NEARBY_NO_FIX);
        }.bind(this),
        { timeout: 8000, maximumAge: 600000 }
      );
    }

    // One request for one rung of the ladder. Resolves to the rows, or to null
    // for anything that went wrong — a first search that fails is a failure,
    // and a widening that fails just leaves the list already on screen.
    requestNearby(lat, lon, step) {
      var url = this.apiBase + '/api/nearby?lat=' + encodeURIComponent(lat) +
        '&lon=' + encodeURIComponent(lon) +
        '&radius_km=' + step.radius_km +
        '&limit=' + step.limit;
      return fetch(url)
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (data) { return data ? (data.results || []) : null; })
        .catch(function () { return null; });
    }

    fetchNearby(lat, lon) {
      this._nearbyOrigin = { lat: lat, lon: lon };
      this._nearbyStep = 0;
      this.requestNearby(lat, lon, NEARBY_STEPS[0])
        .then(function (results) {
          this._nearbyPinned = false;
          this.setNearbyBusy(false);
          if (results === null) {
            this.setNearbyNote(NEARBY_NO_FIX);
            return;
          }
          if (!results.length) {
            // The location worked — there is just no golf inside the radius.
            // Naming the radius is what keeps this from reading as a failure,
            // and with no list there is nothing to pull past, so the wider
            // search has to be offered as the sentence instead.
            this.setNearbyNote(
              'No courses within ' + radiusLabel() + '. Try searching by name.'
            );
            return;
          }
          this.showSuggestions(results, this._nearbyOrigin);
        }.bind(this));
    }

    // Pull past the end of the list: the next rung, rendered in place. The rows
    // are a superset of the ones on screen and in the same order, so the scroll
    // position is kept and the new courses simply arrive underneath.
    expandNearby(done) {
      var step = NEARBY_STEPS[this._nearbyStep + 1];
      var origin = this._nearbyOrigin;
      if (!step || !origin) { done(true); return; }
      var had = this._acItems.length;
      var scrollTop = this.suggestionsEl.scrollTop;
      this.requestNearby(origin.lat, origin.lon, step)
        .then(function (results) {
          if (results === null) { done(false); return; }
          this._nearbyStep += 1;
          var foundMore = results.length > had;
          if (foundMore) {
            this.showSuggestions(results, origin);
            this.suggestionsEl.scrollTop = scrollTop;
          }
          // The end is either no rung left or nothing new to show — the second
          // is why the ladder isn't just counted, since a country with three
          // courses in it finishes at 250km and shouldn't be offered 500.
          done(!foundMore || !NEARBY_STEPS[this._nearbyStep + 1]);
        }.bind(this));
    }

    // ---- Autocomplete -----------------------------------------------------

    onSearchInput() {
      this.stopGhost();
      // Typing is the answer to whatever the nearby line last said.
      this._nearbyPinned = false;
      this.setNearbyNote('');
      // Editing after a pick invalidates it — the buttons must not carry a
      // course the box no longer says.
      if (this.classList.contains('is-chosen')) this.clearChoice();

      clearTimeout(this._acDebounce);
      var q = this.searchEl.value.trim();
      if (q.length < 2) {
        if (this._acAbort) { this._acAbort.abort(); this._acAbort = null; }
        this.setSuggestionsOpen(false);
        return;
      }

      // 150ms debounce + AbortController, matching the designer: a slow
      // response for an older keystroke must not clobber the dropdown after
      // the customer has typed further.
      this._acDebounce = setTimeout(function () {
        if (this._acAbort) this._acAbort.abort();
        this._acAbort = new AbortController();
        var signal = this._acAbort.signal;
        fetch(this.apiBase + '/api/search?q=' + encodeURIComponent(q), { signal })
          .then(function (r) { return r.json(); })
          .then(function (data) {
            if (!signal.aborted) this.showSuggestions(data.results || []);
          }.bind(this))
          .catch(function (err) {
            if (err.name !== 'AbortError') this.setSuggestionsOpen(false);
          }.bind(this));
      }.bind(this), 150);
    }

    // `origin` is set only for a nearby run: the coordinate the list was
    // measured from, which turns each row's own coordinates into the distance
    // that explains the order. A typed search has no origin and no distances.
    showSuggestions(items, origin) {
      this._acItems = items;
      this._acActive = -1;
      this.suggestionsEl.innerHTML = '';
      this.setNearbyNote('');
      if (!items.length) {
        this.setSuggestionsOpen(false);
        return;
      }

      if (origin) {
        // A caption on the set, not an option in it. role="presentation" keeps
        // it out of the listbox, and every query for a row names the suggestion
        // class rather than `li` so the arrow keys never land here.
        var head = document.createElement('li');
        head.className = 'hero-course-search__suggestions-head';
        head.setAttribute('role', 'presentation');
        head.textContent = NEARBY_HEADING;
        this.suggestionsEl.appendChild(head);
      }

      items.forEach(function (item, idx) {
        var li = document.createElement('li');
        li.className = 'hero-course-search__suggestion';
        li.setAttribute('role', 'option');
        li.setAttribute('aria-selected', 'false');
        li.id = this.suggestionsEl.id + '-opt-' + idx;

        var nameEl = document.createElement('span');
        nameEl.className = 'hero-course-search__suggestion-name';
        nameEl.textContent = courseNameOf(item);
        li.appendChild(nameEl);

        // One quiet line under the name carrying both qualifiers: the club,
        // where OSM names it something the course name doesn't already say
        // ("Royal Portrush Golf Club" for "Dunluce Links"), and the place,
        // because course names repeat worldwide. Both are display only — the
        // value carried forward is still the course name.
        var place = coursePlaceOf(item);
        // How far, flush to the right of that same line. It is the sort order
        // made readable — the one thing that says why these courses, and why in
        // this order.
        var coords = origin ? coordsOf(item) : null;
        var distance = coords
          ? formatDistance(distanceKm(origin.lat, origin.lon, coords.lat, coords.lon))
          : '';
        if (item.operator || place || distance) {
          var metaEl = document.createElement('span');
          metaEl.className = 'hero-course-search__suggestion-meta';
          // Club and place share one span so the distance can be the line's
          // other end rather than a third thing in a row of three.
          var qualifiersEl = document.createElement('span');
          qualifiersEl.className = 'hero-course-search__suggestion-qualifiers';
          if (item.operator) {
            var clubEl = document.createElement('span');
            clubEl.className = 'hero-course-search__suggestion-club';
            clubEl.textContent = item.operator;
            qualifiersEl.appendChild(clubEl);
          }
          if (place) {
            var placeEl = document.createElement('span');
            placeEl.className = 'hero-course-search__suggestion-place';
            placeEl.textContent = place;
            qualifiersEl.appendChild(placeEl);
          }
          metaEl.appendChild(qualifiersEl);
          if (distance) {
            var distEl = document.createElement('span');
            distEl.className = 'hero-course-search__suggestion-distance';
            distEl.textContent = distance;
            metaEl.appendChild(distEl);
          }
          li.appendChild(metaEl);
        }

        // mousedown, not click: it has to fire before the input's blur.
        li.addEventListener('mousedown', function (e) {
          e.preventDefault();
          this.selectSuggestion(idx);
        }.bind(this));

        this.suggestionsEl.appendChild(li);
      }.bind(this));

      // Last child, and only over a nearby list: pulling past the end of it is
      // how the radius widens. Re-mounted on every render, because the list is
      // rebuilt from scratch each time.
      var more = this.nearbyMore();
      if (more) {
        more.setEnabled(!!origin);
        if (origin) more.mount(this.suggestionsEl);
      }

      this.setSuggestionsOpen(true);
    }

    // Built on first use, and only where the shared controller was loaded: a
    // theme that hasn't added course-nearby-more.js still gets a working nearby
    // list, it just opens at one radius.
    nearbyMore() {
      if (this._nearbyMore !== undefined) return this._nearbyMore;
      this._nearbyMore = window.CourseNearbyMore
        ? new window.CourseNearbyMore({
          scroller: this.suggestionsEl,
          prefix: 'hero-course-search',
          labels: NEARBY_MORE_LABELS,
          onExpand: this.expandNearby.bind(this),
        })
        : null;
      return this._nearbyMore;
    }

    onSearchKeydown(e) {
      if (this.suggestionsEl.hidden) return;
      // The rows, not every <li>: a nearby list is captioned by one that isn't
      // an option, and counting it would put the arrow keys an index out.
      var lis = this.suggestionsEl.querySelectorAll('.hero-course-search__suggestion');
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.setActive(Math.min(this._acActive + 1, lis.length - 1), lis);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.setActive(Math.max(this._acActive - 1, 0), lis);
      } else if (e.key === 'Enter') {
        // Nothing here is inside a form, so Enter has no default meaning —
        // without this, typing a course and pressing it is a dead end. The
        // top match is what the customer is looking at, so take that.
        e.preventDefault();
        this.selectSuggestion(this._acActive >= 0 ? this._acActive : 0);
      } else if (e.key === 'Escape') {
        this.setSuggestionsOpen(false);
      }
    }

    setActive(idx, lis) {
      Array.prototype.forEach.call(lis, function (li) {
        li.classList.remove('is-active');
        li.setAttribute('aria-selected', 'false');
      });
      if (idx >= 0 && idx < lis.length) {
        lis[idx].classList.add('is-active');
        lis[idx].setAttribute('aria-selected', 'true');
        lis[idx].scrollIntoView({ block: 'nearest' });
        this.searchEl.setAttribute('aria-activedescendant', lis[idx].id);
        this._acActive = idx;
      }
    }

    // ---- Selection --------------------------------------------------------

    selectSuggestion(idx) {
      var item = this._acItems[idx];
      if (!item) return;

      // The name alone, never `label` — that has "(City, Country)" glued on
      // for the dropdown, and this value ends up on the customer's order.
      var name = courseNameOf(item);
      this.searchEl.value = name;
      this.setSuggestionsOpen(false);
      this._acItems = [];
      this.stopGhost();

      // Multi-course clubs: the hero commits to the whole-club map rather than
      // asking which course before the customer has seen anything. The product
      // page loads the picker with this preselected, so the individual courses
      // are one click away at the point where they can see what changes.
      var routing = item.n_routings > 1 ? 'all' : null;

      // One descriptor, written into the links now and sent to /api/prewarm if
      // and when one of them is clicked — the two can't drift apart, which is
      // the invariant prewarm() below depends on.
      // `club`/`place` are display-only qualifiers, carried purely so the
      // product page can offer the same title suggestions a search made there
      // would. Without them the designer arrives with the name and nothing
      // else, and the customer who came through the hero is offered two chips
      // where the same course picked on the product page offers four — the
      // location one above all, which is the chip that tells two courses of the
      // same name apart. Never used to resolve the course: that is what the ids
      // are for.
      this._selection = {
        name: name,
        courseId: item.id != null ? item.id : null,
        osmId: item.osm_id != null ? item.osm_id : null,
        routing: routing,
        club: item.operator ? String(item.operator).trim() : '',
        place: coursePlaceOf(item),
      };
      this._prewarmed = false;

      this.applyLinks(this._selection);
      this.prefetchLinks();
      this.reveal();
      this.syncTrigger();
      // Phone, and the pill at any width: don't send them back to the hero to
      // find the buttons — the garment choice is the next thing on this same
      // screen.
      if (this.endsOnGarments()) this.showGarmentStep();
      document.dispatchEvent(new CustomEvent(CHOSEN_EVENT, {
        detail: { source: this, selection: this._selection },
      }));
      // A desktop hero runs on into the same garment step, in the pill's card:
      // the GIFs choose the garment better than the bare buttons can. With the
      // pill switched off nothing answers, and the buttons slide out as before.
      if (!this.isPill && this._mq && this._mq.matches) {
        this.searchEl.blur();
        document.dispatchEvent(new CustomEvent(GARMENTS_EVENT, { detail: { source: this } }));
      }
    }

    // A garment link was clicked: this course is the one they're going to see.
    onGarmentClick(e) {
      // A plain click leaves this page, and the product page takes a moment to
      // arrive. Without a sign that the tap landed, that moment read as a tap
      // that hadn't, and customers tapped again. A click into a new tab leaves
      // nothing to wait for here.
      if (!e.defaultPrevented && e.button === 0 && !(e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)) {
        this.showLoading(e.currentTarget);
      }

      // Prewarm is off by default: the API now answers fast enough that the
      // product page has little left to hide, so the speculative render buys
      // less than it costs the queue. The machinery stays and one theme
      // setting brings it back. Once per pick — a double click, or a second
      // link after a cmd-click opened the first in a background tab, would
      // only re-send the same descriptor.
      if (!this.prewarmEnabled || !this._selection || this._prewarmed) return;
      this._prewarmed = true;
      this.prewarm(this._selection);
    }

    showLoading(link) {
      if (!link || link.classList.contains('loading')) return;
      this.clearLoading();
      link.classList.add('loading');
      link.setAttribute('aria-busy', 'true');
      if (this.spinnerTpl) {
        // On a garment card the spinner stands in for the name, not over the
        // preview, where it would be lost in a moving picture.
        var host = link.querySelector('.hero-course-search__garment-label') || link;
        host.appendChild(this.spinnerTpl.content.cloneNode(true));
      }
    }

    clearLoading() {
      var box = this.closest('.banner__box') || this;
      Array.prototype.forEach.call(box.querySelectorAll('a.loading'), function (link) {
        link.classList.remove('loading');
        link.removeAttribute('aria-busy');
        var spinner = link.querySelector('.loading__spinner');
        if (spinner) spinner.remove();
      });
    }

    // The product page's HTML is fetched as soon as the course is picked, so
    // the tap on a garment is a navigation to a page already in hand. Prefetch,
    // never prerender: a prerendered product page would run its designer and
    // put a render on the API for every garment the customer didn't choose —
    // the same cost that keeps prewarm switched off.
    prefetchLinks() {
      this.clearPrefetch();
      var urls = this.links
        .map(function (link) { return link.getAttribute('href') || ''; })
        .filter(Boolean);
      if (!urls.length) return;
      var el;
      if (window.HTMLScriptElement && HTMLScriptElement.supports &&
          HTMLScriptElement.supports('speculationrules')) {
        el = document.createElement('script');
        el.type = 'speculationrules';
        el.textContent = JSON.stringify({ prefetch: [{ source: 'list', urls: urls }] });
        document.head.appendChild(el);
        this._prefetchEls = [el];
        return;
      }
      this._prefetchEls = urls.map(function (url) {
        var link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = url;
        document.head.appendChild(link);
        return link;
      });
    }

    clearPrefetch() {
      (this._prefetchEls || []).forEach(function (el) { el.remove(); });
      this._prefetchEls = null;
    }

    // Rewrite each garment link to carry the choice. Relative (path + query)
    // so it survives whichever domain the theme is being previewed on.
    applyLinks(sel) {
      this.links.forEach(function (link, i) {
        var base = this.baseHrefs[i];
        if (!base) return;
        var url = new URL(base, window.location.href);
        url.searchParams.set('course', sel.name);
        if (sel.courseId != null) url.searchParams.set('course_id', sel.courseId);
        if (sel.osmId != null) url.searchParams.set('osm_id', sel.osmId);
        if (sel.routing) url.searchParams.set('routing', sel.routing);
        if (sel.club) url.searchParams.set('club', sel.club);
        if (sel.place) url.searchParams.set('place', sel.place);
        link.setAttribute('href', url.pathname + url.search);
      }.bind(this));
    }

    // Fire-and-forget: the server renders this overview now and parks it, so
    // the product page's own request is a cache hit. Failures are silent -
    // this is an optimisation, not a step. It must also never hold up the
    // navigation it rides on, hence no await and keepalive: true, which is what
    // lets the request outlive the unload it is racing.
    //
    // Must describe the SAME overview applyLinks put in the hrefs, routing
    // included. Warming the club's first course while the link says the whole
    // club renders the wrong map and leaves the slowest case - a multi-course
    // club - paying full price, which is the one this most needs to help.
    prewarm(sel) {
      if (!this.apiBase) return;
      if (sel.courseId == null && sel.osmId == null) return;
      fetch(this.apiBase + '/api/prewarm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          course: sel.name,
          course_id: sel.courseId,
          osm_id: sel.osmId,
          routing: sel.routing,
        }),
        keepalive: true,
      }).catch(function () {});
    }

    // One class on this element; the buttons are moved by a sibling selector
    // off it (component-hero-course-search.css) rather than by a class of
    // their own, so nothing of the section's own styling is touched.
    //
    // Focus stays in the field on a pick, by keyboard or by mouse. Moving it
    // onto the first button would ring it, and a ring on a button that has
    // just slid out reads as "this one is chosen" rather than "these two are
    // now live" — the wrong thing to say about a pair. The buttons follow the
    // field in the DOM, so the next Tab reaches them anyway, and the live
    // region announces that they now carry the course.
    reveal() {
      this.classList.add('is-chosen');

      if (this.statusEl) this.statusEl.textContent = this.dataset.chosenAnnouncement || '';
    }

    // `shared`: the other instance cleared first, so this one doesn't echo it.
    clearChoice(opts) {
      var shared = opts && opts.shared;
      this.classList.remove('is-chosen');
      this.hideGarmentStep();
      // The links go back to bare product URLs, so there is nothing left to
      // warm — and the next pick starts with its own unspent prewarm.
      this._selection = null;
      this._prewarmed = false;
      this.clearPrefetch();
      if (this.statusEl) this.statusEl.textContent = '';
      this.links.forEach(function (link, i) {
        link.setAttribute('href', this.baseHrefs[i]);
      }.bind(this));
      // Runs after _selection is cleared, so the trigger goes back to showing
      // what is in the box rather than the course that is no longer chosen.
      this.syncTrigger();
      if (!shared) {
        document.dispatchEvent(new CustomEvent(CLEARED_EVENT, { detail: { source: this } }));
      }
    }
  }

  customElements.define('hero-course-search', HeroCourseSearch);
})();
